// Raw IndexedDB wrapper for the multi-file store.
// We do NOT use idb-keyval here because we need multi-store transactions
// (docs + binaries + meta in one atomic tx). Thin promise wrappers are enough.

const DB_NAME = "drawOla-multi-file-db";
const DB_VERSION = 1;

export const STORES = {
  DOCS: "docs",
  BINARIES: "binaries",
  META: "meta",
} as const;

/**
 * Keys inside the `meta` store.
 *
 * Declared here — the one module both `multiFileStore.ts` (writer) and
 * `usage.ts` (reader) already depend on — so the two can never drift again.
 * They did: `usage.ts` used to read a hand-typed `"drawOlaUsage"` while the
 * store wrote `"drawolaUsage"`, so every read of the byte ledger returned 0
 * and `canWrite`'s policy-total branch was dead code. See
 * `usage.repairUsageLedger()` for how existing browsers are healed.
 */
export const META_KEYS = {
  ACTIVE: "activeDocId",
  USAGE: "drawolaUsage",
  MIGRATION_VERSION: "migrationVersion",
} as const;

/**
 * The mis-cased usage key that shipped before the fix. Browsers that ran a
 * build in that window may hold a divergent value under it; migration v3
 * deletes it. Never write this key.
 */
export const LEGACY_USAGE_META_KEY = "drawOlaUsage";

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) {
    return dbPromise;
  }
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORES.DOCS)) {
        db.createObjectStore(STORES.DOCS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.BINARIES)) {
        db.createObjectStore(STORES.BINARIES, { keyPath: "fileId" });
      }
      if (!db.objectStoreNames.contains(STORES.META)) {
        db.createObjectStore(STORES.META);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IndexedDB open blocked"));
  });
  return dbPromise;
}

export function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });
}

/** Run work inside a transaction and resolve when tx is complete. */
export async function withTx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  work: (tx: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  const tx = db.transaction(stores, mode);
  let result: T;
  try {
    result = await work(tx);
  } catch (e) {
    tx.abort();
    throw e;
  }
  await txDone(tx);
  return result;
}
